import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Linkedin, Send, ArrowUpRight, MapPin } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { WhatsAppIcon } from "./WhatsApp";
import { CONTACTS, ESIGENZE } from "@/content";
import { useLanguage, validationMessage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { nome: "", azienda: "", email: "", telefono: "", esigenza: "", messaggio: "", privacy: false };

const inputCls =
    "w-full border border-navy-600 bg-navy-950 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-sky-400 focus:ring-1 focus:ring-sky-400/40";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400";

export default function Contact() {
    const { t } = useLanguage();
    const f = t.contact.form;
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const set = (key) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.privacy) {
            setError(f.errorPrivacy);
            return;
        }
        setSending(true);
        try {
            const payload = {
                ...form,
                nome: form.nome.trim(),
                azienda: form.azienda.trim(),
                email: form.email.trim(),
                telefono: form.telefono.trim(),
                messaggio: form.messaggio.trim(),
            };
            await axios.post(`${API}/contact`, payload);
            toast.success(f.toastTitle, {
                description: f.toastDesc,
            });
            setForm(EMPTY);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail)
                ? validationMessage(detail, t)
                : typeof detail === "string"
                  ? detail
                  : f.errorGeneric;
            setError(msg);
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contatti"
            data-testid="contact-section"
            className="relative scroll-mt-20 border-t border-navy-600 bg-navy-900"
        >
            <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 lg:grid-cols-5 lg:px-10 lg:py-32">
                <div className="lg:col-span-2">
                    <SectionHeading
                        chapter={t.contact.chapter}
                        eyebrow={t.contact.eyebrow}
                        title={t.contact.title}
                        description={t.contact.description}
                        testid="contact-heading"
                    />

                    <Reveal delay={0.15}>
                        <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                            {t.contact.direct}
                        </p>
                        <div className="space-y-4">
                            <a
                                href={CONTACTS.phoneHref}
                                data-testid="contact-phone-link"
                                className="group flex items-center justify-between border border-navy-600 bg-navy-950 p-5 transition-colors hover:border-sky-400"
                            >
                                <span className="flex items-center gap-4">
                                    <Phone className="h-5 w-5 text-sky-400" />
                                    <span>
                                        <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                            {t.contact.phoneLabel}
                                        </span>
                                        <span className="mt-1 block font-medium text-slate-100">{CONTACTS.phoneDisplay}</span>
                                    </span>
                                </span>
                                <span className="inline-flex items-center gap-1.5 border border-sky-400/50 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-sky-400 transition-colors group-hover:bg-sky-400 group-hover:text-white">
                                    {t.contact.callLabel}
                                    <ArrowUpRight className="h-3 w-3" />
                                </span>
                            </a>

                            <a
                                href={CONTACTS.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp-link"
                                className="group flex items-center gap-4 border border-navy-600 bg-navy-950 p-5 transition-colors hover:border-[#25D366]"
                            >
                                <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
                                <span>
                                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                        {t.contact.whatsappLabel}
                                    </span>
                                    <span className="mt-1 block font-medium text-slate-100">{t.contact.whatsappText}</span>
                                </span>
                                <ArrowUpRight className="ml-auto h-4 w-4 text-slate-500 transition-colors group-hover:text-[#25D366]" />
                            </a>

                            <a
                                href={`mailto:${CONTACTS.email}`}
                                data-testid="contact-email-link"
                                className="group flex items-center gap-4 border border-navy-600 bg-navy-950 p-5 transition-colors hover:border-sky-400"
                            >
                                <Mail className="h-5 w-5 text-sky-400" />
                                <span>
                                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                        {t.contact.emailLabel}
                                    </span>
                                    <span className="mt-1 block font-medium text-slate-100">{CONTACTS.email}</span>
                                </span>
                            </a>

                            <a
                                href={CONTACTS.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-linkedin-link"
                                className="group flex items-center gap-4 border border-navy-600 bg-navy-950 p-5 transition-colors hover:border-sky-400"
                            >
                                <Linkedin className="h-5 w-5 text-sky-400" />
                                <span>
                                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                        LinkedIn
                                    </span>
                                    <span className="mt-1 block font-medium text-slate-100">Gianluca Capri</span>
                                </span>
                                <ArrowUpRight className="ml-auto h-4 w-4 text-slate-500 transition-colors group-hover:text-sky-400" />
                            </a>

                            <div
                                data-testid="contact-address"
                                className="flex items-center gap-4 border border-navy-600 bg-navy-950 p-5"
                            >
                                <MapPin className="h-5 w-5 text-sky-400" />
                                <span>
                                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                        {t.contact.addressLabel}
                                    </span>
                                    <span className="mt-1 block font-medium text-slate-100">{CONTACTS.address}</span>
                                </span>
                            </div>
                        </div>
                    </Reveal>
                </div>

                <div className="lg:col-span-3">
                    <Reveal delay={0.1}>
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="border border-navy-600 bg-navy-950 p-6 sm:p-8"
                        >
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="nome" className={labelCls}>
                                        {f.name}
                                    </label>
                                    <input
                                        id="nome"
                                        data-testid="contact-name-input"
                                        className={inputCls}
                                        value={form.nome}
                                        onChange={set("nome")}
                                        required
                                        placeholder={f.namePlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="azienda" className={labelCls}>
                                        {f.company}
                                    </label>
                                    <input
                                        id="azienda"
                                        data-testid="contact-company-input"
                                        className={inputCls}
                                        value={form.azienda}
                                        onChange={set("azienda")}
                                        required
                                        placeholder={f.companyPlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className={labelCls}>
                                        {f.email}
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        data-testid="contact-email-input"
                                        className={inputCls}
                                        value={form.email}
                                        onChange={set("email")}
                                        required
                                        placeholder={f.emailPlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="telefono" className={labelCls}>
                                        {f.phone}
                                    </label>
                                    <input
                                        id="telefono"
                                        type="tel"
                                        data-testid="contact-phone-input"
                                        className={inputCls}
                                        value={form.telefono}
                                        onChange={set("telefono")}
                                        placeholder="+39 ..."
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="esigenza" className={labelCls}>
                                        {f.need}
                                    </label>
                                    <select
                                        id="esigenza"
                                        data-testid="contact-need-select"
                                        className={`${inputCls} appearance-none`}
                                        value={form.esigenza}
                                        onChange={set("esigenza")}
                                        required
                                    >
                                        <option value="" disabled>
                                            {f.selectPlaceholder}
                                        </option>
                                        {ESIGENZE.map((value, i) => (
                                            <option key={value} value={value}>
                                                {t.contact.needs[i]}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="messaggio" className={labelCls}>
                                        {f.message}
                                    </label>
                                    <textarea
                                        id="messaggio"
                                        data-testid="contact-message-textarea"
                                        className={`${inputCls} min-h-[130px] resize-y`}
                                        value={form.messaggio}
                                        onChange={set("messaggio")}
                                        required
                                        placeholder={f.messagePlaceholder}
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex items-start gap-3">
                                <input
                                    id="privacy-check"
                                    type="checkbox"
                                    data-testid="contact-privacy-checkbox"
                                    checked={form.privacy}
                                    onChange={set("privacy")}
                                    className="mt-0.5 h-4 w-4 border-navy-600 bg-navy-950 accent-sky-400"
                                />
                                <label htmlFor="privacy-check" className="cursor-pointer text-sm text-slate-400">
                                    {f.privacyA}
                                    <a
                                        href="/privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-privacy-link"
                                        className="text-sky-400 underline underline-offset-2 transition-colors hover:text-sky-300"
                                    >
                                        {f.privacyLink}
                                    </a>
                                    {f.privacyB}
                                </label>
                            </div>

                            {error && (
                                <p data-testid="contact-form-error" className="mt-4 text-sm text-red-400">
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                data-testid="contact-submit-button"
                                disabled={sending}
                                className="mt-7 inline-flex w-full items-center justify-center gap-2 bg-sky-400 px-7 py-4 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {sending ? f.sending : f.submit}
                                <Send className="h-4 w-4" />
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
